/**
 * Gulcan PDP JavaScript
 * Version: 2.0.0 - Simplified and debugged
 */

(function() {
  'use strict';

  console.log('Gulcan PDP: Script loaded');

  // State
  let product = null;
  let media = [];
  let currentMediaIndex = 0;
  let stickyCartObserver = null;
  let documentEscapeBound = false;
  let initGeneration = 0;

  // Initialize when DOM is ready
  function init() {
    console.log('Gulcan PDP: Initializing...');
    const generation = ++initGeneration;

    const container = document.querySelector('[data-gulcan-pdp]');
    if (!container) {
      console.log('Gulcan PDP: No container found');
      return;
    }

    console.log('Gulcan PDP: Container found');

    // A sibling swap replaces the full main section. Reset every module value
    // before reading the new product so no previous product can leak into ATC.
    product = null;
    media = [];
    currentMediaIndex = 0;
    isAnimating = false;

    const sectionWrapper = container.closest('[id^="shopify-section-"]') || document;

    // Product and media scripts sit outside the visual PDP container.
    // Scope both reads to the current section wrapper after every swap.
    const productJson = sectionWrapper.querySelector('[data-product-json]');
    if (productJson) {
      try {
        product = JSON.parse(productJson.textContent);
        console.log('Gulcan PDP: Product loaded', product.title);
        // Recently viewed tracking moved to gulcan-header.js
      } catch (e) {
        console.error('Gulcan PDP: Failed to parse product JSON', e);
      }
    }

    const mediaJson = sectionWrapper.querySelector('[data-media-json]');
    if (mediaJson) {
      try {
        media = JSON.parse(mediaJson.textContent);
        console.log('Gulcan PDP: Media loaded', media.length, 'items');
      } catch (e) {
        console.error('Gulcan PDP: Failed to parse media JSON', e);
      }
    }

    // Bind all events
    bindVariantEvents(container);
    bindGalleryEvents(container);
    bindQuantityEvents(container);
    bindFormEvents(container);
    bindStickyCart(container);
    bindZoomEvents(container);
    bindKlarnaModal();
    bindDocumentEscape();
    bindAccordions();
    bindWishlistButton(container);

    // Check initial image brightness for breadcrumb color
    detectImageBrightness(container);

    // Initialize delivery estimate
    initDeliveryEstimate(generation);

    console.log('Gulcan PDP: Initialization complete');
  }

  // ========================================
  // IMAGE BRIGHTNESS DETECTION
  // ========================================

  /**
   * Detects the brightness of the top-left corner of the main image
   * and applies appropriate class for breadcrumb text color
   */
  function detectImageBrightness(container) {
    const galleryMain = container.querySelector('[data-gallery-main]');
    const mainImage = container.querySelector('[data-main-image]');

    if (!galleryMain || !mainImage) return;

    // Wait for image to load if needed
    if (!mainImage.complete) {
      mainImage.addEventListener('load', () => analyzeImageBrightness(mainImage, galleryMain));
    } else {
      analyzeImageBrightness(mainImage, galleryMain);
    }
  }

  function analyzeImageBrightness(img, galleryMain) {
    try {
      // Create canvas to sample image pixels
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Sample top-left corner of the image (where breadcrumbs overlay)
      const sampleWidth = 200;
      const sampleHeight = 60;

      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      // Simple approach: sample top-left corner of the source image
      // This works because product photos typically have consistent backgrounds
      ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight, 0, 0, sampleWidth, sampleHeight);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
      const data = imageData.data;

      // Calculate average brightness
      let totalBrightness = 0;
      let pixelCount = 0;

      for (let i = 0; i < data.length; i += 4) {
        // Calculate perceived brightness (human eye is more sensitive to green)
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
        totalBrightness += brightness;
        pixelCount++;
      }

      const avgBrightness = totalBrightness / pixelCount;

      // Apply class based on brightness (threshold around 128 = middle gray)
      // Lower threshold (100) means only truly dark images get white text
      if (avgBrightness < 100) {
        galleryMain.classList.add('image-dark');
        galleryMain.classList.remove('image-light');
      } else {
        galleryMain.classList.add('image-light');
        galleryMain.classList.remove('image-dark');
      }

      console.log('Gulcan PDP: Image brightness:', avgBrightness.toFixed(0), avgBrightness < 100 ? '(dark)' : '(light)');
    } catch (e) {
      // Cross-origin or other errors - default to dark text
      console.log('Gulcan PDP: Could not analyze image brightness', e.message);
      galleryMain.classList.add('image-light');
      galleryMain.classList.remove('image-dark');
    }
  }

  // ========================================
  // VARIANT SELECTION
  // ========================================

  function bindVariantEvents(container) {
    // Color swatches
    const swatches = container.querySelectorAll('.gulcan-variant-swatch');
    console.log('Gulcan PDP: Found', swatches.length, 'swatches');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        handleVariantClick(this, container);
      });
    });

    // Option buttons
    const buttons = container.querySelectorAll('.gulcan-variant-button');
    console.log('Gulcan PDP: Found', buttons.length, 'variant buttons');

    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        handleVariantClick(this, container);
      });
    });
  }

  function handleVariantClick(element, container) {
    const optionIndex = parseInt(element.dataset.optionIndex, 10);
    const optionValue = element.dataset.optionValue;

    console.log('Gulcan PDP: Variant clicked', optionIndex, optionValue);

    // Update active state in the group
    const group = element.closest('.gulcan-variant-group');
    if (group) {
      group.querySelectorAll('.gulcan-variant-swatch, .gulcan-variant-button').forEach(el => {
        el.classList.remove('active', 'selected');
      });
      element.classList.add('active', 'selected');

      // Update label
      const label = group.querySelector('[data-selected-option]');
      if (label) {
        label.textContent = optionValue;
      }
    }

    // Find matching variant
    if (product && product.variants) {
      const selectedOptions = getSelectedOptions(container);
      const variant = findVariant(selectedOptions);

      if (variant) {
        console.log('Gulcan PDP: Found variant', variant.id, variant.title);
        updateVariant(variant, container);
      }
    }
  }

  function getSelectedOptions(container) {
    const options = [];
    const groups = container.querySelectorAll('.gulcan-variant-group');

    groups.forEach((group, index) => {
      const active = group.querySelector('.gulcan-variant-swatch.active, .gulcan-variant-button.active');
      if (active) {
        options[index] = active.dataset.optionValue;
      }
    });

    return options;
  }

  function findVariant(selectedOptions) {
    if (!product || !product.variants) return null;

    return product.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant.options[index] === option;
      });
    });
  }

  function updateVariant(variant, container) {
    // Update hidden input
    const variantInput = container.querySelector('[data-variant-id]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price
    const priceEl = container.querySelector('[data-price]');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Update sticky price
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      stickyPrice.textContent = formatMoney(variant.price);
    }

    // Update compare price
    const compareEl = container.querySelector('[data-compare-price]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Update discount badge (-XX%) — recalculated per variant since variants can differ
    const badgeEl = container.querySelector('[data-discount-badge]');
    if (badgeEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        badgeEl.textContent = '-' + pct + '%';
        badgeEl.style.display = '';
      } else {
        badgeEl.style.display = 'none';
      }
    }

    // Update add to cart button
    const addBtn = container.querySelector('[data-add-to-cart]');
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');

    if (variant.available) {
      if (addBtn) {
        addBtn.disabled = false;
        const btnText = addBtn.querySelector('.btn-text');
        if (btnText) btnText.textContent = 'In winkelwagen';
      }
      if (stickyBtn) {
        stickyBtn.disabled = false;
        const stickyBtnText = stickyBtn.querySelector('.btn-text');
        if (stickyBtnText) stickyBtnText.textContent = 'Toevoegen';
      }
    } else {
      if (addBtn) {
        addBtn.disabled = true;
        const btnText = addBtn.querySelector('.btn-text');
        if (btnText) btnText.textContent = 'Uitverkocht';
      }
      if (stickyBtn) {
        stickyBtn.disabled = true;
        const stickyBtnText = stickyBtn.querySelector('.btn-text');
        if (stickyBtnText) stickyBtnText.textContent = 'Uitverkocht';
      }
    }

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Update gallery if variant has featured image
    if (variant.featured_media) {
      const mediaIndex = media.findIndex(m => m.id === variant.featured_media.id);
      if (mediaIndex !== -1) {
        showMedia(mediaIndex, container);
      }
    }
  }

  // ========================================
  // GALLERY
  // ========================================

  // Cache for preloaded images
  const imageCache = new Map();

  function bindGalleryEvents(container) {
    // Preload all gallery images for instant switching
    preloadGalleryImages();

    // Thumbnails
    const thumbs = container.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function() {
        const index = parseInt(this.dataset.mediaIndex, 10);
        showMedia(index, container);
      });
    });

    // Prev/Next buttons
    const prevBtn = container.querySelector('[data-gallery-prev]');
    const nextBtn = container.querySelector('[data-gallery-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => navigateGallery(-1, container));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => navigateGallery(1, container));
    }

    // Touch swipe
    initTouchSwipe(container);
  }

  // Preload all gallery images for instant switching
  function preloadGalleryImages() {
    media.forEach((item, index) => {
      if (item.src && !imageCache.has(item.src)) {
        const img = new Image();
        img.src = item.src;
        imageCache.set(item.src, img);
      }
    });
    console.log('Gulcan PDP: Preloaded', imageCache.size, 'gallery images');
  }

  function navigateGallery(direction, container) {
    let newIndex = currentMediaIndex + direction;
    if (newIndex < 0) newIndex = media.length - 1;
    if (newIndex >= media.length) newIndex = 0;
    showMedia(newIndex, container);
  }

  // Track if animation is in progress to prevent rapid clicks
  let isAnimating = false;

  function showMedia(index, container, direction = null) {
    if (index < 0 || index >= media.length) return;
    if (index === currentMediaIndex) return; // Already showing this image
    if (isAnimating) return; // Animation in progress

    // Determine slide direction if not specified
    if (direction === null) {
      direction = index > currentMediaIndex ? 'left' : 'right';
    }

    const item = media[index];
    const galleryMain = container.querySelector('[data-gallery-main]');
    const oldImage = container.querySelector('[data-main-image]');

    if (oldImage && item.src && galleryMain) {
      const animationGeneration = initGeneration;
      isAnimating = true;
      galleryMain.classList.add('animating');

      // Create new image that will become the main image
      const newImage = document.createElement('img');
      newImage.crossOrigin = 'anonymous'; // Allow canvas analysis for breadcrumb color
      newImage.src = item.src;
      newImage.alt = item.alt || '';
      newImage.dataset.mainImage = '';
      newImage.dataset.zoomSrc = item.zoomSrc || item.src;
      newImage.style.position = 'absolute';
      newImage.style.top = '0';
      newImage.style.left = '0';
      newImage.style.width = '100%';
      newImage.style.height = '100%';
      newImage.style.objectFit = 'contain';
      newImage.style.transition = 'transform 0.3s ease-out';

      // Position new image off-screen based on direction
      // "left" direction = going to next = new image comes from right
      if (direction === 'left') {
        newImage.style.transform = 'translateX(100%)';
      } else {
        newImage.style.transform = 'translateX(-100%)';
      }

      // Add new image to gallery
      galleryMain.appendChild(newImage);

      // Ensure old image has transition
      oldImage.style.transition = 'transform 0.3s ease-out';

      // Trigger animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Slide new image in to center
          newImage.style.transform = 'translateX(0)';

          // Slide old image out in opposite direction
          if (direction === 'left') {
            oldImage.style.transform = 'translateX(-100%)';
          } else {
            oldImage.style.transform = 'translateX(100%)';
          }

          // After animation completes, clean up
          setTimeout(() => {
            // Remove old image completely
            oldImage.remove();

            // Make new image the permanent main image
            newImage.style.position = '';
            newImage.style.top = '';
            newImage.style.left = '';
            newImage.style.width = '';
            newImage.style.height = '';
            newImage.style.transform = '';
            newImage.style.transition = '';

            galleryMain.classList.remove('animating');
            if (animationGeneration === initGeneration) {
              isAnimating = false;
            }

            // Re-analyze brightness for new image (wait for load if needed)
            if (newImage.complete && newImage.naturalWidth > 0) {
              analyzeImageBrightness(newImage, galleryMain);
            } else {
              newImage.addEventListener('load', () => {
                analyzeImageBrightness(newImage, galleryMain);
              });
            }
          }, 300); // Match CSS transition duration
        });
      });
    }

    currentMediaIndex = index;

    // Update thumbnails
    const thumbs = container.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });

    // Update counter
    const counter = container.querySelector('[data-current-index]');
    if (counter) {
      counter.textContent = index + 1;
    }
  }

  function initTouchSwipe(container) {
    const gallery = container.querySelector('[data-gallery-main]');
    if (!gallery) return;

    let startX = 0;

    gallery.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    gallery.addEventListener('touchend', e => {
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          navigateGallery(1, container);
        } else {
          navigateGallery(-1, container);
        }
      }
    }, { passive: true });
  }

  // ========================================
  // QUANTITY
  // ========================================

  function bindQuantityEvents(container) {
    const input = container.querySelector('[data-quantity-input]');
    const btns = container.querySelectorAll('.gulcan-quantity-btn');

    btns.forEach(btn => {
      btn.addEventListener('click', function() {
        if (!input) return;

        let value = parseInt(input.value, 10) || 1;
        const action = this.dataset.action;

        if (action === 'increase') {
          value++;
        } else if (action === 'decrease' && value > 1) {
          value--;
        }

        input.value = value;
      });
    });
  }

  // ========================================
  // FORM / ADD TO CART
  // ========================================

  function bindFormEvents(container) {
    const form = container.querySelector('[data-product-form]');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      addToCart(this, container);
    });
  }

  function addToCart(form, container) {
    // Safety net: if a swatch was clicked but the hidden input somehow didn't
    // catch up, recompute the variant id from the currently active swatches
    // before submitting. This guards against a race where the click handler
    // and the form submit fire in the same tick on touch devices.
    let variantId = form.querySelector('[name="id"]').value;
    if (product && product.variants) {
      const liveVariant = findVariant(getSelectedOptions(container));
      if (liveVariant && String(liveVariant.id) !== String(variantId)) {
        console.log('Gulcan PDP: Reconciling stale variant id', variantId, '->', liveVariant.id);
        variantId = liveVariant.id;
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) variantInput.value = liveVariant.id;
      }
    }
    const quantityInput = container.querySelector('[data-quantity-input]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    const addBtn = container.querySelector('[data-add-to-cart]');
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');

    // Loading state
    if (addBtn) addBtn.classList.add('loading');
    if (stickyBtn) stickyBtn.classList.add('loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('Gulcan PDP: Added to cart', data);

      // Success state
      if (addBtn) {
        addBtn.classList.remove('loading');
        addBtn.classList.add('added');
      }
      if (stickyBtn) {
        stickyBtn.classList.remove('loading');
        stickyBtn.classList.add('added');
      }

      setTimeout(() => {
        if (addBtn) addBtn.classList.remove('added');
        if (stickyBtn) stickyBtn.classList.remove('added');
      }, 2000);

      // Trigger Impulse theme cart events
      // Impulse theme listens for these events to update cart UI and open drawer
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));

      // Update cart count (for header cart icon)
      updateCartCount();

      // Open Impulse theme's cart drawer if available
      // Impulse exposes theme.CartDrawer for cart drawer functionality
      openImpulseCartDrawer();
    })
    .catch(error => {
      console.error('Gulcan PDP: Add to cart failed', error);
      if (addBtn) addBtn.classList.remove('loading');
      if (stickyBtn) stickyBtn.classList.remove('loading');
      alert('Er ging iets mis. Probeer het opnieuw.');
    });
  }

  /**
   * Open Impulse theme's cart drawer
   * Impulse theme exposes theme.CartDrawer for cart drawer functionality
   * We try multiple methods to ensure compatibility across Impulse versions
   */
  function openImpulseCartDrawer() {
    // Method 1: Try Impulse's CartDrawer directly (if initialized)
    if (typeof theme !== 'undefined' && theme.CartDrawer) {
      // CartDrawer might be a class instance or need initialization
      if (typeof theme.CartDrawer.open === 'function') {
        console.log('Gulcan PDP: Opening cart drawer via theme.CartDrawer.open()');
        theme.CartDrawer.open();
        return;
      }
      // Try the prototype method if CartDrawer is a class
      if (theme.CartDrawer.prototype && typeof theme.CartDrawer.prototype.open === 'function') {
        // Need to find the instance - Impulse usually stores it
        const drawerInstance = document.querySelector('[data-cart-drawer]')?.__cartDrawer;
        if (drawerInstance) {
          console.log('Gulcan PDP: Opening cart drawer via instance');
          drawerInstance.open();
          return;
        }
      }
    }

    // Method 2: Trigger Impulse's cart drawer via click on cart icon
    // This is a fallback that works because it simulates user interaction
    const cartLink = document.querySelector('.js-drawer-open-cart, [data-cart-open], .site-header__cart');
    if (cartLink) {
      console.log('Gulcan PDP: Opening cart drawer via cart link click');
      cartLink.click();
      return;
    }

    // Method 3: Dispatch Impulse-specific drawer open event
    // Impulse listens for 'drawer:open' events
    console.log('Gulcan PDP: Dispatching drawer:open event');
    document.dispatchEvent(new CustomEvent('drawer:open', {
      detail: { type: 'cart' }
    }));
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('.cart-link__count, .cart-count, [data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
      })
      .catch(e => console.error('Gulcan PDP: Cart count update failed', e));
  }

  // ========================================
  // STICKY CART
  // ========================================

  function bindStickyCart(container) {
    const stickyCart = document.querySelector('[data-sticky-cart]');
    const addToCartBtn = container.querySelector('[data-add-to-cart]');
    const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');

    if (stickyCartObserver) {
      stickyCartObserver.disconnect();
      stickyCartObserver = null;
    }
    document.body.classList.remove('gulcan-sticky-active');

    if (!stickyCart || !addToCartBtn) return;

    // Show/hide sticky cart based on add to cart button visibility
    const observerGeneration = initGeneration;
    stickyCartObserver = new IntersectionObserver(entries => {
      if (observerGeneration !== initGeneration) return;
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          stickyCart.classList.add('visible');
          document.body.classList.add('gulcan-sticky-active');
        } else {
          stickyCart.classList.remove('visible');
          document.body.classList.remove('gulcan-sticky-active');
        }
      });
    }, { threshold: 0 });

    stickyCartObserver.observe(addToCartBtn);

    // Sticky button triggers form submit
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function() {
        const form = container.querySelector('[data-product-form]');
        if (form) {
          form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      });
    }
  }

  // ========================================
  // ZOOM
  // ========================================

  function bindZoomEvents(container) {
    const zoomBtn = container.querySelector('[data-zoom-trigger]');
    const zoomModal = document.querySelector('[data-zoom-modal]');
    const zoomClose = document.querySelector('[data-zoom-close]');
    const zoomImage = document.querySelector('[data-zoom-image]');
    const mainImage = container.querySelector('[data-main-image]');

    if (zoomBtn && zoomModal) {
      zoomBtn.addEventListener('click', () => openZoom(mainImage, zoomImage, zoomModal));
    }

    if (mainImage && zoomModal) {
      mainImage.addEventListener('click', () => {
        if (window.innerWidth >= 768) {
          openZoom(mainImage, zoomImage, zoomModal);
        }
      });
    }

    if (zoomClose) {
      zoomClose.addEventListener('click', () => closeZoom(zoomModal));
    }

    if (zoomModal) {
      zoomModal.addEventListener('click', e => {
        if (e.target === zoomModal) closeZoom(zoomModal);
      });
    }
  }

  function openZoom(mainImage, zoomImage, modal) {
    if (!mainImage || !zoomImage || !modal) return;
    zoomImage.src = mainImage.dataset.zoomSrc || mainImage.src;
    zoomImage.alt = mainImage.alt;
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  function closeZoom(modal) {
    if (!modal) return;
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  // ========================================
  // KLARNA MODAL
  // ========================================

  function bindKlarnaModal() {
    const trigger = document.querySelector('[data-klarna-modal-trigger]');
    const modal = document.querySelector('[data-klarna-modal]');
    const closeBtns = modal ? modal.querySelectorAll('[data-klarna-modal-close]') : [];
    const overlay = modal ? modal.querySelector('.klarna-modal-overlay') : null;

    if (!trigger || !modal) {
      console.log('Gulcan PDP: Klarna modal elements not found');
      return;
    }

    console.log('Gulcan PDP: Binding Klarna modal');

    // Open modal on trigger click
    trigger.addEventListener('click', function(e) {
      e.preventDefault();
      openKlarnaModal(modal);
    });

    // Close modal on any close button click (X button and bottom button)
    closeBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        closeKlarnaModal(modal);
      });
    });

    // Close modal on overlay click
    if (overlay) {
      overlay.addEventListener('click', function() {
        closeKlarnaModal(modal);
      });
    }

  }

  /**
   * Bind one document-level Escape listener for replaceable PDP modals.
   * The listener resolves the current modal nodes at event time.
   */
  function bindDocumentEscape() {
    if (documentEscapeBound) return;
    documentEscapeBound = true;

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;

      const zoomModal = document.querySelector('[data-zoom-modal]');
      if (zoomModal && zoomModal.classList.contains('active')) {
        closeZoom(zoomModal);
      }

      const klarnaModal = document.querySelector('[data-klarna-modal]');
      if (klarnaModal && klarnaModal.classList.contains('active')) {
        closeKlarnaModal(klarnaModal);
      }
    });
  }

  function openKlarnaModal(modal) {
    if (!modal) return;
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    console.log('Gulcan PDP: Klarna modal opened');
  }

  function closeKlarnaModal(modal) {
    if (!modal) return;
    modal.classList.remove('active');
    document.body.style.overflow = '';
    console.log('Gulcan PDP: Klarna modal closed');
  }

  // ========================================
  // ACCORDIONS
  // ========================================

  function bindAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    if (accordions.length === 0) {
      console.log('Gulcan PDP: No accordions found');
      return;
    }

    console.log('Gulcan PDP: Found', accordions.length, 'accordions');

    accordions.forEach(accordion => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');

      if (trigger) {
        trigger.addEventListener('click', function() {
          toggleAccordion(accordion);
        });
      }
    });

    // Check if URL has #reviews hash, open reviews accordion
    if (window.location.hash === '#reviews') {
      const reviewsAccordion = document.getElementById('reviews');
      if (reviewsAccordion) {
        // Small delay to ensure page has loaded
        setTimeout(() => {
          openAccordion(reviewsAccordion);
          reviewsAccordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }

  function toggleAccordion(accordion) {
    const isOpen = accordion.classList.contains('open');
    const trigger = accordion.querySelector('[data-accordion-trigger]');

    if (isOpen) {
      // Closing manually - scroll to this accordion after close
      closeAccordion(accordion, true);
    } else {
      // Opening - will auto-close others and scroll to new one
      openAccordion(accordion);
    }

    // Update aria-expanded
    if (trigger) {
      trigger.setAttribute('aria-expanded', !isOpen);
    }
  }

  function scrollToAccordion(accordion) {
    setTimeout(() => {
      const headerHeight = 70; // Sticky nav height
      const targetY = accordion.getBoundingClientRect().top + window.scrollY - headerHeight;

      // Custom smooth scroll with easing for smoother feel
      smoothScrollTo(targetY, 600); // 600ms duration
    }, 350); // Wait for CSS transition
  }

  // Custom smooth scroll with easing
  function smoothScrollTo(targetY, duration) {
    const startY = window.scrollY;
    const difference = targetY - startY;
    const startTime = performance.now();

    function easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    function step(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      window.scrollTo(0, startY + (difference * easedProgress));

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  function openAccordion(accordion) {
    // Close all other accordions first (only one open at a time)
    // Use autoClose = true so they don't trigger their own scroll
    const allAccordions = document.querySelectorAll('[data-accordion]');

    allAccordions.forEach(other => {
      if (other !== accordion && other.classList.contains('open')) {
        closeAccordion(other, false); // false = auto-close, don't scroll to it
        // Update aria-expanded on the closed accordion's trigger
        const otherTrigger = other.querySelector('[data-accordion-trigger]');
        if (otherTrigger) {
          otherTrigger.setAttribute('aria-expanded', 'false');
        }
      }
    });

    // Open the clicked accordion
    accordion.classList.add('open');

    // Always scroll to the newly opened accordion
    scrollToAccordion(accordion);

    console.log('Gulcan PDP: Accordion opened');
  }

  function closeAccordion(accordion, isManualClose = false) {
    accordion.classList.remove('open');

    // If manually closed (not auto-closed by opening another), scroll to this accordion
    if (isManualClose) {
      scrollToAccordion(accordion);
    }

    console.log('Gulcan PDP: Accordion closed');
  }

  // ========================================
  // WISHLIST BUTTON (Swish Integration)
  // ========================================

  /**
   * Binds the wishlist button click handler
   * Integrates with Swish (Wishlist King) app
   */
  function bindWishlistButton(container) {
    const wishlistBtn = container.querySelector('[data-wishlist-btn]');
    if (!wishlistBtn) return;

    const productId = wishlistBtn.dataset.productId;
    const variantId = wishlistBtn.dataset.variantId;

    // Check if product is already in wishlist (on page load)
    checkWishlistStatus(wishlistBtn, productId);

    // Handle click
    wishlistBtn.addEventListener('click', function() {
      toggleWishlist(this, productId, variantId);
    });

    console.log('Gulcan PDP: Wishlist button bound for product', productId);
  }

  /**
   * Check if product is already in user's wishlist
   * Uses Swish/WishlistKing API if available
   */
  function checkWishlistStatus(button, productId) {
    // Method 1: Check via Swish/WishlistKing global
    if (typeof window.WishlistKing !== 'undefined') {
      window.WishlistKing.toolkit.getProduct(productId, function(product) {
        if (product && product.inWishlist) {
          button.classList.add('is-active');
        }
      });
      return;
    }

    // Method 2: Check localStorage fallback (if Swish not loaded yet)
    const localWishlist = getLocalWishlist();
    if (localWishlist.includes(productId)) {
      button.classList.add('is-active');
    }

    // Method 3: Listen for Swish ready event and re-check
    document.addEventListener('swish:ready', function() {
      if (typeof window.WishlistKing !== 'undefined') {
        window.WishlistKing.toolkit.getProduct(productId, function(product) {
          if (product && product.inWishlist) {
            button.classList.add('is-active');
          }
        });
      }
    }, { once: true });
  }

  /**
   * Toggle product in/out of wishlist
   */
  function toggleWishlist(button, productId, variantId) {
    const isActive = button.classList.contains('is-active');
    const countEl = button.querySelector('[data-wishlist-count]');

    if (isActive) {
      // Remove from wishlist
      removeFromWishlist(button, productId, variantId);
      button.classList.remove('is-active');

      // Decrement count display
      if (countEl) {
        const currentCount = parseInt(countEl.textContent, 10) || 0;
        countEl.textContent = Math.max(0, currentCount - 1);
      }
    } else {
      // Add to wishlist
      addToWishlist(button, productId, variantId);
      button.classList.add('is-active');

      // Increment count display
      if (countEl) {
        const currentCount = parseInt(countEl.textContent, 10) || 0;
        countEl.textContent = currentCount + 1;
      }
    }
  }

  /**
   * Add product to wishlist via Swish API
   */
  function addToWishlist(button, productId, variantId) {
    console.log('Gulcan PDP: Adding to wishlist', productId);

    // Method 1: Use Swish/WishlistKing API
    if (typeof window.WishlistKing !== 'undefined') {
      window.WishlistKing.toolkit.addProduct(productId, variantId);
      return;
    }

    // Method 2: Trigger Swish's add button if exists on page
    const swishAddBtn = document.querySelector('[data-wk-add-product="' + productId + '"], .wk-add-button');
    if (swishAddBtn) {
      swishAddBtn.click();
      return;
    }

    // Method 3: Store in localStorage as fallback
    const localWishlist = getLocalWishlist();
    if (!localWishlist.includes(productId)) {
      localWishlist.push(productId);
      localStorage.setItem('gulcan_wishlist', JSON.stringify(localWishlist));
    }

    console.log('Gulcan PDP: Added to local wishlist (Swish not available)');
  }

  /**
   * Remove product from wishlist via Swish API
   */
  function removeFromWishlist(button, productId, variantId) {
    console.log('Gulcan PDP: Removing from wishlist', productId);

    // Method 1: Use Swish/WishlistKing API
    if (typeof window.WishlistKing !== 'undefined') {
      window.WishlistKing.toolkit.removeProduct(productId);
      return;
    }

    // Method 2: Store in localStorage as fallback
    const localWishlist = getLocalWishlist();
    const index = localWishlist.indexOf(productId);
    if (index > -1) {
      localWishlist.splice(index, 1);
      localStorage.setItem('gulcan_wishlist', JSON.stringify(localWishlist));
    }
  }

  /**
   * Get local wishlist from localStorage (fallback when Swish not available)
   */
  function getLocalWishlist() {
    try {
      const stored = localStorage.getItem('gulcan_wishlist');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  // ========================================
  // UTILITY
  // ========================================

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + amount;
  }

  // ========================================
  // DELIVERY ESTIMATE
  // ========================================

  /**
   * Lead times in hours from Sendcloud API (cached)
   * Updated: 2025-12-22
   */
  /**
   * Transit days per country, copied from the shop's own shipping rates.
   *
   * Shopify serves these live at /cart/shipping_rates.json as delivery_days,
   * but only once the cart holds something, and a product page is usually the
   * first stop. So the numbers live here as well, and the pair has to be kept
   * equal by hand: a rate changed in Shopify without changing this table makes
   * the product page promise something the checkout will not repeat.
   */
  const DELIVERY_LEAD_TIMES = {
    NL: { minDays: 1, maxDays: 4 },
    BE: { minDays: 2, maxDays: 7 },
    DE: { minDays: 2, maxDays: 6 },
    FR: { minDays: 3, maxDays: 8 },
    AT: { minDays: 3, maxDays: 10 },
    DK: { minDays: 4, maxDays: 10 },
    SE: { minDays: 4, maxDays: 12 },
    // A country the shop does not list a rate for cannot be promised anything
    // narrower than its slowest real destination.
    DEFAULT: { minDays: 4, maxDays: 12 }
  };

  /**
   * Country names in Dutch
   */
  const COUNTRY_NAMES = {
    NL: 'Nederland',
    BE: 'België',
    DE: 'Duitsland',
    FR: 'Frankrijk',
    AT: 'Oostenrijk',
    LU: 'Luxemburg',
    IT: 'Italië',
    ES: 'Spanje',
    PT: 'Portugal',
    PL: 'Polen',
    CZ: 'Tsjechië',
    DK: 'Denemarken',
    SE: 'Zweden',
    FI: 'Finland',
    IE: 'Ierland',
    GB: 'Verenigd Koninkrijk',
    CH: 'Zwitserland'
  };

  /**
   * Minimum shipping costs per country (cheapest Sendcloud option + €0.40 admin)
   * These are base rates - actual rates may vary by weight/size
   * Updated: 2025-01-24
   */
  const SHIPPING_COSTS = {
    NL: 0,        // Gratis verzending
    BE: 6.95,     // PostNL/DPD base + €0.40
    DE: 8.95,     // DHL/DPD base + €0.40
    FR: 12.95,    // DPD base + €0.40
    AT: 12.95,    // DPD base + €0.40
    LU: 8.95,     // PostNL base + €0.40
    // Default for other countries
    DEFAULT: 14.95
  };

  // Cutoff time for same-day dispatch (14:00 Amsterdam time)
  const CUTOFF_HOUR = 14;

  // Dutch month names (short)
  const MONTHS_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  const DAYS_NL = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

  /**
   * Initialize delivery estimate
   */
  function initDeliveryEstimate(generation) {
    // Check for URL parameter override (for testing: ?country=DE)
    const urlParams = new URLSearchParams(window.location.search);
    const testCountry = urlParams.get('country');

    if (testCountry) {
      if (generation === initGeneration) {
        calculateAndDisplayDelivery(testCountry.toUpperCase());
      }
      return;
    }

    // Detect country via IP geolocation
    detectCountry()
      .then(countryCode => {
        if (generation === initGeneration) {
          calculateAndDisplayDelivery(countryCode);
        }
      })
      .catch(() => {
        if (generation === initGeneration) {
          calculateAndDisplayDelivery('NL');
        }
      });
  }

  /**
   * Detect user's country via free IP geolocation APIs.
   * Tries multiple providers because ipapi.co rate-limits aggressively
   * (returns 429 with no CORS header on busy days). api.country.is is
   * a robust CORS-friendly free alternative.
   */
  async function detectCountry() {
    const providers = [
      {
        url: 'https://ipapi.co/country_code/',
        parse: async (r) => (await r.text()).trim().toUpperCase()
      },
      {
        url: 'https://api.country.is/',
        parse: async (r) => ((await r.json()).country || '').toUpperCase()
      },
      {
        url: 'https://ipwho.is/',
        parse: async (r) => ((await r.json()).country_code || '').toUpperCase()
      }
    ];

    for (const p of providers) {
      try {
        const response = await fetch(p.url);
        if (!response.ok) continue;
        const code = await p.parse(response);
        if (code && code.length === 2) return code;
      } catch (e) {
        // Silent fail, try next provider
      }
    }

    // All failed, fall back to NL
    return 'NL';
  }

  /**
   * Read the per-product extra handling/lead days from the timeline element.
   * Liquid renders data-lead-days="N" from a "verzending-Ndag" product tag.
   * Returns 0 for in-stock products with standard same-/next-day dispatch.
   */
  function getProductLeadDays() {
    const timeline = document.querySelector('[data-delivery-timeline]');
    if (!timeline) return 0;
    const raw = parseInt(timeline.getAttribute('data-lead-days'), 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  /**
   * Shop-wide extra working days for a busy or holiday period, rendered by
   * Liquid from the theme settings. Zero when the setting is off.
   */
  function getBusyExtraDays() {
    const timeline = document.querySelector('[data-delivery-timeline]');
    if (!timeline) return 0;
    const raw = parseInt(timeline.getAttribute('data-busy-extra-days'), 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  /**
   * Calculate delivery dates and update UI
   */
  function calculateAndDisplayDelivery(countryCode) {
    const leadTime = DELIVERY_LEAD_TIMES[countryCode] || DELIVERY_LEAD_TIMES.DEFAULT;
    const productLeadDays = getProductLeadDays();

    // Get current date in Amsterdam timezone
    const now = new Date();
    const amsterdamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    const currentHour = amsterdamTime.getHours();

    // Determine dispatch date
    let dispatchDate = new Date(amsterdamTime);

    // If after cutoff time, dispatch is next business day
    if (currentHour >= CUTOFF_HOUR) {
      dispatchDate = getNextBusinessDay(dispatchDate);
    }

    // If today is weekend, move to next business day
    const dayOfWeek = dispatchDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      dispatchDate = getNextBusinessDay(dispatchDate);
    }

    // Per-product handling time: items with a verzending-Ndag tag need N extra
    // working days in the warehouse before they leave (dropship, pre-order, etc.)
    if (productLeadDays > 0) {
      dispatchDate = addBusinessDays(dispatchDate, productLeadDays);
    }

    // Dispatch can be same day or +1 business day (busy warehouse)
    const dispatchDateMin = new Date(dispatchDate);
    const dispatchDateNormalMax = getNextBusinessDay(dispatchDate);

    // A busy or holiday period is uncertainty, not a known delay: some orders
    // leave on time and others wait on staffing. Widening only the late end
    // keeps the promise honest either way, where shifting both would claim a
    // slowness the shop cannot predict and would under-promise every parcel
    // that does go out normally.
    const busyExtraDays = getBusyExtraDays();
    const dispatchDateMax = busyExtraDays > 0
      ? addBusinessDays(dispatchDateNormalMax, busyExtraDays)
      : dispatchDateNormalMax;

    // A shipping rate in Shopify carries one number, so the shop's warehouse
    // backlog had to be stuffed into the same field as the carrier's transit.
    // Here there are two steps, so the backlog is put back where it happens:
    // the parcel leaves later, the carrier is as quick as ever. Transit shrinks
    // by exactly the days dispatch gained, which leaves the latest delivery on
    // the same day the checkout promises.
    // Both ends measure the same journey: the earliest dispatch plus the fastest
    // transit, the latest dispatch plus the slowest. Reading the early end from
    // the late dispatch made the two rows contradict each other, promising a
    // parcel could leave on Tuesday yet not arrive until Thursday.
    const transitMaxDays = Math.max(leadTime.minDays, leadTime.maxDays - busyExtraDays);
    const minDeliveryDate = addBusinessDays(dispatchDateMin, leadTime.minDays);
    const maxDeliveryDate = addBusinessDays(dispatchDateMax, transitMaxDays);

    // Format dates
    const orderedText = 'Vandaag';
    const shippedText = formatDateRangeNL(dispatchDateMin, dispatchDateMax);
    const deliveredText = formatDateRangeNL(minDeliveryDate, maxDeliveryDate);

    // Update timeline UI
    updateDeliveryUI(orderedText, shippedText, deliveredText);

    // Update shipping cost display
    updateShippingCostUI(countryCode);
  }

  /**
   * Update shipping cost display based on country
   * NL = "Gratis verzending"
   * Other = "Verzending vanaf €X,XX" + country name
   */
  function updateShippingCostUI(countryCode) {
    const shippingCost = SHIPPING_COSTS[countryCode] ?? SHIPPING_COSTS.DEFAULT;
    const countryName = COUNTRY_NAMES[countryCode] || countryCode;
    const isNL = countryCode === 'NL';

    // Find all shipping text elements
    const shippingElements = document.querySelectorAll('[data-shipping-text]');

    shippingElements.forEach(el => {
      if (isNL) {
        el.innerHTML = 'Gratis verzending';
      } else {
        // Format price with € symbol
        const priceFormatted = shippingCost.toFixed(2).replace('.', ',');
        el.innerHTML = `Verzending vanaf €${priceFormatted} <small>in ${countryName}</small>`;
      }
    });

    // Also update trust badges that show shipping info
    const trustShippingBadges = document.querySelectorAll('[data-shipping-badge]');
    trustShippingBadges.forEach(el => {
      if (isNL) {
        el.textContent = 'Gratis verzending';
      } else {
        el.textContent = 'Snelle verzending';
      }
    });

    // Update shipping subtitles (show country for non-NL)
    const shippingSubtitles = document.querySelectorAll('[data-shipping-subtitle]');
    shippingSubtitles.forEach(el => {
      if (isNL) {
        el.textContent = 'boven €100 in Nederland';
      } else {
        const priceFormatted = shippingCost.toFixed(2).replace('.', ',');
        el.textContent = `vanaf €${priceFormatted} in ${countryName}`;
      }
    });

    // Update product features shipping text
    // Uses specific data attribute for reliable targeting
    const featureShippingText = document.querySelector('[data-feature-shipping-text]');
    if (featureShippingText) {
      if (isNL) {
        featureShippingText.textContent = 'Gratis vanaf €100';
      } else {
        const priceFormatted = shippingCost.toFixed(2).replace('.', ',');
        featureShippingText.textContent = `Vanaf €${priceFormatted}`;
      }
    }

    // Update shipping accordion country and price
    // Uses specific data attributes for reliable targeting
    const countryNameEl = document.querySelector('[data-shipping-country-name]');
    const countryValueEl = document.querySelector('[data-shipping-country-value]');

    if (countryNameEl) {
      countryNameEl.textContent = countryName;
    }

    if (countryValueEl) {
      if (isNL) {
        countryValueEl.textContent = 'Gratis verzending vanaf €100';
      } else {
        const priceFormatted = shippingCost.toFixed(2).replace('.', ',');
        countryValueEl.textContent = `Verzendkosten vanaf €${priceFormatted}`;
      }
    }

    // Store detected country for use in cart
    window.gulcanDetectedCountry = countryCode;
    window.gulcanShippingCost = shippingCost;

    // Dispatch event so cart drawer can update
    document.dispatchEvent(new CustomEvent('gulcan:countryDetected', {
      detail: { countryCode, shippingCost, countryName }
    }));
  }

  /**
   * Get next business day (skip Saturday and Sunday)
   */
  function getNextBusinessDay(date) {
    const next = new Date(date);
    next.setDate(next.getDate() + 1);

    // Skip weekend
    while (next.getDay() === 0 || next.getDay() === 6) {
      next.setDate(next.getDate() + 1);
    }

    return next;
  }

  /**
   * Add business days to a date
   */
  function addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      // Count only weekdays
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        added++;
      }
    }

    return result;
  }

  /**
   * Format date in Dutch (e.g., "ma 23 dec")
   */
  function formatDateNL(date) {
    const day = DAYS_NL[date.getDay()];
    const dayNum = date.getDate();
    const month = MONTHS_NL[date.getMonth()];
    return `${day} ${dayNum} ${month}`;
  }

  /**
   * Format date range in Dutch (e.g., "di 24 - do 26 dec")
   */
  function formatDateRangeNL(minDate, maxDate) {
    const minDay = DAYS_NL[minDate.getDay()];
    const minDayNum = minDate.getDate();
    const maxDay = DAYS_NL[maxDate.getDay()];
    const maxDayNum = maxDate.getDate();
    const month = MONTHS_NL[maxDate.getMonth()];

    // If same day, show single date
    if (minDate.toDateString() === maxDate.toDateString()) {
      return `${minDay} ${minDayNum} ${month}`;
    }

    return `${minDay} ${minDayNum} - ${maxDay} ${maxDayNum} ${month}`;
  }

  /**
   * Update the delivery timeline and sticky bar UI
   */
  function updateDeliveryUI(orderedText, shippedText, deliveredText) {
    // Update timeline
    const orderedEl = document.querySelector('[data-ordered-date]');
    const shippedEl = document.querySelector('[data-shipped-date]');
    const deliveredEl = document.querySelector('[data-delivered-date]');

    if (orderedEl) orderedEl.textContent = orderedText;
    if (shippedEl) shippedEl.textContent = shippedText;
    if (deliveredEl) deliveredEl.textContent = deliveredText;

    // Mark ordered step as active
    const orderedStep = document.querySelector('[data-step="ordered"]');
    if (orderedStep) orderedStep.classList.add('active', 'completed');

    // Update sticky bar delivery text
    const stickyDelivery = document.querySelector('[data-sticky-delivery-text]');
    if (stickyDelivery) {
      stickyDelivery.textContent = `Bezorgd: ${deliveredText}`;
    }

    // Update accordion delivery estimate
    const accordionDelivery = document.querySelector('[data-accordion-delivery-estimate]');
    if (accordionDelivery) {
      accordionDelivery.textContent = deliveredText;
    }
  }

  // ========================================
  // INIT
  // ========================================
  // Note: Recently viewed tracking moved to gulcan-header.js

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload
  document.addEventListener('shopify:section:load', e => {
    const target = e.target;
    if (target && (
      (target.matches && target.matches('[data-gulcan-pdp]')) ||
      (target.querySelector && target.querySelector('[data-gulcan-pdp]'))
    )) {
      init();
    }
  });

})();
